// SEO Configuration File
// Contains default values and schemas for structured data (search + AI discovery).
import {
  APP_NAME,
  APP_TITLE,
  TAGLINE,
  TAGLINE_SENTENCE,
  SEO_DESCRIPTION,
  SITE_ORIGIN,
  DEFAULT_OG_IMAGE_URL,
} from "../helpers/constants";

const ORG_ID = `${SITE_ORIGIN}/#organization`;

// Default SEO values: title/imageAlt use APP_TITLE from constants
export const defaultSeo = {
  title: APP_TITLE,
  description: SEO_DESCRIPTION,
  keywords: `LandlordOps, AI answering service for landlords, AI first responder for landlords, automated maintenance triage, rent collection, Schedule E, vendor bidding, Leo AI, Magic Link, self-managing landlord, Rental Command Center`,
  image: DEFAULT_OG_IMAGE_URL,
  imageAlt: APP_TITLE,
  url: `${SITE_ORIGIN}/`,
  type: "website",
  locale: "en_US",
  twitterHandle: `@useLandlordOps`,
  siteName: APP_NAME,
};

/** Feature list aligned with product marketing; helps LLMs and search match queries to capabilities. */
export const SOFTWARE_FEATURE_LIST = [
  "Full LandlordOps OS on every tier: rent collection, Schedule E, vendors, ledgers, and Leo AI",
  "Tenant Magic Link with Leo AI, knowledge base, chat and triage on every tier",
  "Emergency escalation: email, SMS, and voice on every plan",
  "Automated Rent Collection and Ledger Syncing on Free through Scale",
  "Schedule E Tax Reporting and vendor management on every plan",
  "Door caps: Free 3 · Starter 15 · Growth 50 · Scale 150",
];

// Common structured data schemas
export const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": ORG_ID,
  name: APP_NAME,
  alternateName: ["Landlord Ops", "LandlordOps AI"],
  url: `${SITE_ORIGIN}/`,
  description: SEO_DESCRIPTION,
  logo: `${SITE_ORIGIN}/logo512.png`,
  sameAs: [
    `https://twitter.com/useLandlordOps`,
    `https://www.linkedin.com/company/landlordops`,
  ],
  knowsAbout: [
    "LandlordOps property management software",
    "LandlordOps tenant AI messaging",
    "AI answering service for landlords",
    "AI first responder for landlords",
    "automated maintenance triage",
    "real estate Closing Vault",
    "Magic Link tenant chat for landlords",
    "Starter Growth Scale pricing",
    "Leo AI emergency escalation",
  ],
  contactPoint: {
    "@type": "ContactPoint",
    contactType: "customer service",
    email: "support@landlordops.com",
    availableLanguage: "English",
  },
};

/** WebSite: helps crawlers and assistants associate the domain with the product entity. */
export const websiteSchema = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: APP_NAME,
  alternateName: TAGLINE,
  url: `${SITE_ORIGIN}/`,
  description: SEO_DESCRIPTION,
  inLanguage: "en-US",
  publisher: { "@id": ORG_ID },
};

export const softwareApplicationSchema = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  name: APP_NAME,
  alternateName: [
    TAGLINE,
    "AI first responder for landlords",
    "Landlord Ops",
  ],
  applicationCategory: "BusinessApplication",
  applicationSubCategory: "PropertyManagementSoftware",
  operatingSystem: "Web",
  url: `${SITE_ORIGIN}/`,
  downloadUrl: `${SITE_ORIGIN}/signup`,
  screenshot: DEFAULT_OG_IMAGE_URL,
  description: SEO_DESCRIPTION,
  featureList: SOFTWARE_FEATURE_LIST,
  audience: {
    "@type": "BusinessAudience",
    audienceType:
      "Independent landlords, real estate investors, small portfolio owners, property managers evaluating AI tools",
    industry: "Real Estate Rental Property Management",
  },
  offers: {
    "@type": "Offer",
    price: "0",
    priceCurrency: "USD",
    description: `Monthly subscription plans with door (unit) limits: Free 3 · Starter 15 · Growth 50 · Scale 150. Every plan includes the full LandlordOps OS - Leo AI, rent collection, vendors, ledgers, and Schedule E exports. Cancel anytime per plan terms. See ${SITE_ORIGIN}/.`,
    url: `${SITE_ORIGIN}/`,
  },
  provider: { "@id": ORG_ID },
};

// FAQ Schema - discovery-oriented questions first (search + AI assistants)
export const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    {
      "@type": "Question",
      name: `What is ${APP_NAME}?`,
      acceptedAnswer: {
        "@type": "Answer",
        text: `${APP_NAME} is ${TAGLINE_SENTENCE}. Every plan includes the full LandlordOps OS: Leo AI behind a tenant Magic Link, automated rent collection, vendor bidding, in-app ledgers, Schedule E-oriented exports, and portfolio analytics. Tiers differ only by door count (Free 3 · Starter 15 · Growth 50 · Scale 150). Built for independent landlords. Pairs cleanly with tools you already use.`,
      },
    },
    {
      "@type": "Question",
      name: `What is Leo AI in ${APP_NAME}?`,
      acceptedAnswer: {
        "@type": "Answer",
        text: `Leo is ${APP_NAME}'s built-in AI assistant for tenants on every subscription tier. Leo answers routine questions, uses your knowledge base, helps with maintenance intake and tickets, escalates when needed, and ties into Stripe rent workflows and maintenance orchestration on every plan. It uses tenant, unit, and lease context to respond accurately.`,
      },
    },
    {
      "@type": "Question",
      name: `Who is ${APP_NAME} for?`,
      acceptedAnswer: {
        "@type": "Answer",
        text: `${APP_NAME} is for self-managing landlords and investors with small to mid-size rental portfolios (Free 3 · Starter 15 · Growth 50 · Scale 150 doors). Every plan includes the full OS - rent collection, Schedule E, vendors, ledgers, and Leo AI. Upgrade only when you need more doors.`,
      },
    },
    {
      "@type": "Question",
      name: `Does ${APP_NAME} integrate with AppFolio, Yardi, or Buildium?`,
      acceptedAnswer: {
        "@type": "Answer",
        text: `${APP_NAME} does not currently provide automated import or API sync from AppFolio, Yardi, or Buildium. Many customers use ${APP_NAME} in parallel: they keep their existing software for accounting or compliance and use ${APP_NAME} for Leo, leasing, analytics, or other workflows they choose to run here.`,
      },
    },
    {
      "@type": "Question",
      name: `How does ${APP_NAME} help you stop carrying rentals in your head?`,
      acceptedAnswer: {
        "@type": "Answer",
        text: `${APP_NAME} helps you track decisions, follow-ups, and what happens next in one place - especially when automation isn't enough. Instead of remembering what's outstanding, tracking follow-ups, and juggling messages across texts and emails, everything lives in one place. You check it when you need to, not because you're worried you forgot something.`,
      },
    },
    {
      "@type": "Question",
      name: `Is ${APP_NAME} secure for handling tenant and financial data?`,
      acceptedAnswer: {
        "@type": "Answer",
        text: `Yes, ${APP_NAME} uses enterprise-grade security with encrypted data storage, secure payment processing via Stripe (PCI compliant), and follows industry best practices for data protection. All tenant information and financial data are stored securely and only accessible by authorized users.`,
      },
    },
    {
      "@type": "Question",
      name: "What asset management tasks can Leo AI handle?",
      acceptedAnswer: {
        "@type": "Answer",
        text: `Leo AI answers tenant questions and handles triage on all tiers. Coordinating maintenance end-to-end, sending rent reminders, deep portfolio analytics, and automated rent collection are included on every plan. Upgrade only when you need more doors. See pricing on the website.`,
      },
    },
    {
      "@type": "Question",
      name: "How does automated rent collection work?",
      acceptedAnswer: {
        "@type": "Answer",
        text: `Every plan of ${APP_NAME} integrates with Stripe for automated rent collection: landlords connect Stripe, tenants get payment links and reminders, payments are tracked in real time, and late fees can apply per your rules. Upgrade only when your portfolio needs a higher door cap.`,
      },
    },
    {
      "@type": "Question",
      name: `What types of properties can ${APP_NAME} manage?`,
      acceptedAnswer: {
        "@type": "Answer",
        text: `${APP_NAME} can manage any type of rental property including single-family homes, multi-family assets, apartments, condos, townhouses, and commercial properties. The platform supports multiple properties and assets, making it ideal for independent investors managing portfolios of any size. Our platform continuously adds features based on investor needs.`,
      },
    },
    {
      "@type": "Question",
      name: "How quickly can I integrate LandlordOps with my existing property management workflow?",
      acceptedAnswer: {
        "@type": "Answer",
        text: `Most landlords are live in minutes: add doors, share magic links, configure your knowledge base, and connect Stripe for rent collection - all on every plan. You can run ${APP_NAME} alongside other PM or accounting tools and choose which workflows to centralize here.`,
      },
    },
    {
      "@type": "Question",
      name: "Does LandlordOps integrate with my existing payment and accounting systems?",
      acceptedAnswer: {
        "@type": "Answer",
        text: `Every plan of ${APP_NAME} integrates with Stripe for rent collection and card/ACH processing. You can export tax-ready and financial summaries for your CPA or tools like QuickBooks. Door caps are the only difference between Free, Starter, Growth, and Scale.`,
      },
    },
    {
      "@type": "Question",
      name: "What kind of analytics and reporting does the platform provide?",
      acceptedAnswer: {
        "@type": "Answer",
        text: `Full portfolio analytics, including rent roll health, DSCR, NOI, and maintenance status, is included on every plan. Free, Starter, Growth, and Scale differ only by how many doors you can manage.`,
      },
    },
  ],
};

// Product schema for the visualization tool
export const productSchema = {
  "@context": "https://schema.org",
  "@type": "Product",
  name: `${APP_NAME} - ${TAGLINE}`,
  description: `${APP_NAME}: ${TAGLINE_SENTENCE}. Every plan includes the full OS - DSCR and NOI, maintenance and vendors, Stripe rent collection, Schedule E exports, and Leo AI tenant triage. Tiers differ by door count only.`,
  image: DEFAULT_OG_IMAGE_URL,
  brand: {
    "@type": "Brand",
    name: APP_NAME,
  },
  audience: {
    "@type": "BusinessAudience",
    industry: "Real Estate, Property Investors, Asset Management",
  },
  offers: {
    "@type": "Offer",
    price: "0",
    priceCurrency: "USD",
    availability: "https://schema.org/InStock",
    description: `Monthly subscriptions with door limits on Starter, Growth, and Scale. See ${SITE_ORIGIN}/ for pricing.`,
  },
};

// Service schema for the provider matching service
export const serviceSchema = {
  "@context": "https://schema.org",
  "@type": "Service",
  serviceType: `Rental Command Center`,
  provider: {
    "@type": "Organization",
    name: APP_NAME,
    url: SITE_ORIGIN,
  },
  description: `${TAGLINE_SENTENCE}. Monthly plans with door limits. Full LandlordOps OS - rent, vendors, ledgers, Schedule E, and Leo - on every tier.`,
  areaServed: {
    "@type": "Country",
    name: "United States",
  },
  audience: {
    "@type": "Audience",
    audienceType: `Independent Landlords, Real Estate Investors, Portfolio & Asset Management`,
  },
};

// Page-specific schema generation functions
export const generateHomeSchema = () => {
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: defaultSeo.title,
    description: defaultSeo.description,
    keywords: defaultSeo.keywords,
    url: defaultSeo.url,
    primaryImageOfPage: {
      "@type": "ImageObject",
      url: defaultSeo.image,
      width: "1200",
      height: "630",
    },
    mainEntity: {
      "@type": "SoftwareApplication",
      name: `${APP_NAME}`,
      description: SEO_DESCRIPTION,
      applicationCategory: "BusinessApplication",
      featureList: SOFTWARE_FEATURE_LIST,
      targetAudience: [
        "Investors with 1-50 assets",
        "Landlords using PM tools who still feel like the mental hub",
        "Property operators seeking to reduce cognitive load",
      ],
      offers: {
        "@type": "Offer",
        name: "Monthly subscription",
        price: "0",
        priceCurrency: "USD",
        description:
          "Free, Starter, Growth, or Scale: door limits per tier, full LandlordOps OS on every plan, cancel anytime per plan terms",
      },
    },
  };
};

export const generateProviderPageSchema = () => {
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: `${TAGLINE} | ${APP_NAME}`,
    description: `${TAGLINE_SENTENCE}. ${APP_NAME} delivers live DSCR and NOI, maintenance routing, and tenant payments in one platform.`,
    keywords: `rental command center, portfolio yield, DSCR NOI, landlord software, rent collection, tenant payments, property management`,
    url: `${defaultSeo.url}/providers`,
    mainEntity: {
      "@type": "Service",
      name: `${APP_NAME} - ${TAGLINE}`,
      description: `${TAGLINE_SENTENCE}. Portfolio yield, DSCR, and tenant payments in one place.`,
      provider: {
        "@type": "Organization",
        name: APP_NAME,
        url: SITE_ORIGIN,
      },
    },
  };
};

export const generateGallerySchema = () => {
  return {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: `Asset Management Resources | ${APP_NAME}`,
    description: `Browse our collection of asset management resources, guides, and best practices using ${APP_NAME}. See how our platform helps investors manage portfolio yield and operations.`,
    url: `${defaultSeo.url}/resources`,
    mainEntity: {
      "@type": "CollectionPage",
      name: `${APP_NAME} Asset Management Resources`,
      description: `Resources and guides for AI asset management and portfolio yield from ${APP_NAME}.`,
    },
  };
};

export const generateMissionSchema = () => {
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: `Our Mission | ${APP_NAME}`,
    description: `Learn about ${APP_NAME}'s mission to transform property management for independent landlords through ${TAGLINE_SENTENCE}: rent collection, maintenance, and tenant communication.`,
    url: `${defaultSeo.url}/mission`,
    mainEntity: {
      "@type": "Organization",
      name: APP_NAME,
      description: `${TAGLINE_SENTENCE}. One platform for portfolio and asset management.`,
    },
  };
};

// Breadcrumb schema generator
export const generateBreadcrumbSchema = (items) => {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: item.url,
    })),
  };
};
